import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { motion, AnimatePresence } from "framer-motion";
import adimage1 from '../../assets/pageimages/roomanywhere/raindeximg1.png';

const fallbackData = [
  {
    param1: 2018,
    items: [
      { label: "Virat Kohli", value: 920 },
      { label: "Joe Root", value: 860 },
      { label: "Kane Williamson", value: 855 },
      { label: "Steve Smith", value: 830 },
      { label: "Babar Azam", value: 780 },
    ],
  },
  {
    param1: 2019,
    items: [
      { label: "Virat Kohli", value: 928 },
      { label: "Steve Smith", value: 911 },
      { label: "Kane Williamson", value: 890 },
      { label: "Joe Root", value: 860 },
      { label: "Babar Azam", value: 800 },
    ],
  },
  {
    param1: 2020,
    items: [
      { label: "Steve Smith", value: 919 },
      { label: "Virat Kohli", value: 892 },
      { label: "Kane Williamson", value: 883 },
      { label: "Babar Azam", value: 820 },
      { label: "Joe Root", value: 800 },
    ],
  },
  {
    param1: 2021,
    items: [
      { label: "Joe Root", value: 917 },
      { label: "Kane Williamson", value: 901 },
      { label: "Steve Smith", value: 891 },
      { label: "Babar Azam", value: 850 },
      { label: "Virat Kohli", value: 830 },
    ],
  },
  {
    param1: 2022,
    items: [
      { label: "Babar Azam", value: 902 },
      { label: "Joe Root", value: 885 },
      { label: "Steve Smith", value: 870 },
      { label: "Kane Williamson", value: 860 },
      { label: "Virat Kohli", value: 820 },
    ],
  },
];

const DataJoyHome = ({
  dataset,
  headline = "Dynamic Ranking Chart",
  param1Label = "Year",
  param2Label = "Points",
}) => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const frames = dataset && dataset.length > 0 ? dataset : fallbackData;
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const current = frames[index] || { param1: "", items: [] };

  const maxValue = useMemo(
    () => Math.max(...current.items.map((c) => c.value), 0),
    [current.items]
  );

  const sortedItems = useMemo(
    () => [...current.items].sort((a, b) => b.value - a.value),
    [current.items]
  );

  useEffect(() => {
    if (!frames.length) return;

    const interval = setInterval(() => {
      if (!isPausedRef.current) {
        setIndex((prev) => (prev + 1) % frames.length);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [frames]); 

  if (!frames.length) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-900 text-white">
        <header className="w-full max-w-6xl mx-auto px-4 bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
          <div className="py-4 flex flex-col items-start">
            <h1 className="text-xl lg:text-xl font-bold text-yellow-200">📊 DataJoy</h1>
            <h2 className="text-sm lg:text-lg text-white">Create. Publish. Record. Empower User With Your Own Data!</h2>
          </div>
        </header>
        <main className="flex-1 w-full max-w-6xl mx-auto px-4 flex items-center justify-center">
          <p className="text-sm">No data available</p>
        </main>
        <footer className="w-full max-w-6xl mx-auto px-4 bg-gray-900 border-t border-gray-800">
          <div className="py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-gray-400">
            <div>© 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</div>
            <div className="flex flex-wrap gap-4">
              <Link to="/about-us" className="hover:text-teal-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'about':
        return (
          <>
            <div className="max-w-none">
                <h2 className="text-m lg:text-lg text-yellow-200 font-bold mb-4">About DataJoy</h2>
                <p className="text-xs lg:text-sm leading-relaxed">
                DataJoy is an innovative platform designed to empower users with their data. 
                Create dynamic visualizations, publish interactive charts, record time-series data, 
                and share insights effortlessly. Whether you're tracking sports rankings like 
                cricket player points over years or analyzing your own datasets, DataJoy brings 
                the joy of data to life through smooth animations and intuitive interfaces.
                </p>
                <p className="text-xs lg:text-sm leading-relaxed mt-4">
                Built with React and Framer Motion, it supports horizontal bar charts that 
                cycle through frames every 3 seconds, with pause/resume on hover for desktop 
                and touch for mobile. Upload your own data to customize rankings and discover 
                patterns in your information.
                </p>
            </div>
            <div
                className="mt-4 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
                onClick={() => navigate("/roomanywhere")}
                >
                <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-gray-700">
                    <img
                    src={adimage1} 
                    alt="Ad"
                    className="w-full h-full object-cover"
                    />
                </div>
                <div className="flex flex-col text-left">
                    <p className="text-xs lg:text-sm uppercase text-gray-400 mb-1">Think5Whys Ad</p>
                    <p className="font-semibold text-xs lg:text-sm"><span className="text-yellow-200">RoomAnywhere</span>: Make Indoor and Outdoor Room (Patent Applied)</p>
                    <p className="text-xs lg:text-sm mt-1">Click to see more!</p>
                </div>
            </div>
        </>
        );
      case 'upload':
        return (
            <>
                <div className="text-center py-2">
                    <h2 className="text-m lg:text-lg text-yellow-200 font-bold mb-2">Upload Your Data</h2>
                    <p className="text-sm lg:text-m text-orange-500 mb-4">Coming soon: Upload your CSV or JSON datasets to create custom dynamic charts.</p>
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-auto">
                    <h3 className="font-semibold mb-2">Supported Formats</h3>
                    <ul className="text-left text-sm space-y-1">
                        <li>• CSV: Year, Player, Points</li>
                        <li>• JSON: Array of frames with param1 and items</li>
                    </ul>
                    </div>
                </div>
                <div
                className="mt-4 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
                onClick={() => navigate("/roomanywhere")}
                >
                    <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-gray-700">
                        <img
                        src={adimage1} 
                        alt="Ad"
                        className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex flex-col text-left">
                        <p className="text-xs lg:text-sm uppercase text-gray-400 mb-1">Think5Whys Ad</p>
                        <p className="font-semibold text-xs lg:text-sm"><span className="text-yellow-200">RoomAnywhere</span>: Make Indoor and Outdoor Room (Patent Applied)</p>
                        <p className="text-xs lg:text-sm mt-1">Click to see more!</p>
                    </div>
                </div>
            </>          
        );
      case 'joy':
        return (
            <>
                <div className="w-full max-w-xl flex flex-col items-center mb-0">
                    <h1 className="text-m lg:text-lg font-bold text-center">{headline}</h1>
                    <p className="text-sm lg:text-lg mt-1 opacity-80">
                    {param1Label}: {current.param1}
                    </p>
                </div>           
            </>
        );
      default:
        return null;
    }
  };

  const renderChart = activeTab === 'joy';

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-white overflow-hidden">
      <header className="w-full max-w-6xl mx-auto px-4 bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="py-4 flex flex-col items-start">
          <h1 className="text-lg lg:text-xl font-bold text-yellow-200">📊 DataJoy</h1>
          <h2 className="text-xs lg:text-lg text-white">Create. Publish. Record. Empower User With Your Own Data!</h2>
        </div>
        {/* Tab Navigation */}
        <nav className="flex border-b border-gray-700 pb-2">
          {[
            { key: 'about', label: 'About DataJoy' },
            { key: 'upload', label: 'Upload Your Data' },
            { key: 'joy', label: 'Joy of Data' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium mx-1 rounded-t-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-indigo-600 text-white border-b-2 border-indigo-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 overflow-y-auto justify-center items-center text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
            {renderChart && (
              <div
                className="w-full max-w-xl space-y-3 mt-8"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <AnimatePresence>
                  {sortedItems.map((c) => (
                    <motion.div
                      key={c.label}
                      layout
                      layoutId={c.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.6 }}
                      className="flex items-center gap-2"
                    >
                      <span className="min-w-0 truncate text-left text-xs sm:text-sm w-28">
                        {c.label}
                      </span>
                      <div className="flex-1 h-3 sm:h-4 bg-gray-800 rounded-xl overflow-hidden">
                        <motion.div
                          className="h-full bg-indigo-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${(c.value / maxValue) * 100}%` }}
                          transition={{ duration: 0.8 }}
                        />
                      </div>
                      <span className="text-xs sm:text-sm w-24 text-right">
                        {c.value} {param2Label}
                      </span>
                    </motion.div>
                  ))}
                </AnimatePresence>
                <div className="mt-2 border-t border-blue-600"><p className="text-xs text-blue-400 lg:text-sm py-2">Unique View Count: 125697</p></div>
                <div
                    className="mt-2 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
                    onClick={() => navigate("/roomanywhere")}
                >
                    <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-gray-700">
                        <img
                        src={adimage1} 
                        alt="Ad"
                        className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex flex-col text-left">
                        <p className="text-xs lg:text-sm uppercase text-gray-400 mb-1">Think5Whys Ad</p>
                        <p className="font-semibold text-xs lg:text-sm"><span className="text-yellow-200">RoomAnywhere</span>: Make Indoor and Outdoor Room (Patent Applied)</p>
                        <p className="text-xs lg:text-sm mt-1">Click to see more!</p>
                    </div>
                </div>    
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>
      <footer className="w-full max-w-6xl mx-auto px-4 bg-gray-900 border-t border-gray-800 sticky bottom-0 z-40">
        <div className="py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-xs lg:text-sm text-gray-400">
          <div>© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataJoyHome;